import { Fragment } from 'react'
import type { JSX } from 'react'

import type { PriceExplanation, ReturnsPosition, SaleType, Tender } from '@lensmira/domain'

import { formatDayTime } from '@lensmira/i18n'
import type { MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'
import { sentence } from '../sentence.js'
import { PriceExplanationList } from './price-explanation.js'
import { returnsPositionLabel, tenderLabel } from '../status-labels.js'

/**
 * Order detail: the lines, their money, the payments taken, and what is outstanding. §6.7;
 * `SAL-010`, `SAL-012`; Appendix V.1.
 *
 * **There is no `GET` for this yet.** A read endpoint is being written in parallel with this
 * screen, so `order` arrives as a prop, already resolved, the same way `JobDetail` is handed a
 * `job` and `CashSession` a `session`. Loading it, and keeping it current, is the caller's job.
 *
 * **This is a read, so it does not go through the write queue.** `OFF-001a` is about writes, and
 * this screen makes none: nothing here is a button that enqueues, and nothing is an input.
 *
 * **Sale type and returns position are per line, not per order, and frozen at order time.**
 * `SAL-012`: "each line carries its own type, its own Rx requirement and its own returns
 * position." So `OrderDetailLine` carries both directly, and this screen never looks either up
 * from `packages/domain`'s `SALE_TYPE_DEFINITIONS`: that registry can change after this order was
 * placed, and the frozen value on the line is the one the sale was actually made under.
 *
 * **The price explanation is Appendix V.1's structured breakdown, stored on the line, rendered
 * only when present.** It is what the counter shows when a customer asks why a lens costs what it
 * costs. It is shown exactly as `packages/domain`'s pricing engine produced it, and deliberately
 * not through the same path as every other amount on this screen: `PriceExplanation.deltaMinor`
 * and `.runningMinor` are named "Minor" but are **not** settled minor units. The engine's own
 * `formatExact` rounds only once, at its own step 11 ("The only rounding in the engine"), so every
 * explanation entry before that step is an exact fraction, up to four decimal places. Running one
 * through `../money.ts`'s `parseMinorUnits` would both throw (it accepts only `/^-?\d+$/`) and add
 * a rounding step this screen has no licence to take: `MR-04` spends the whole "exactly three
 * points" budget on the settled totals below, none of them here. So explanation amounts go through
 * `formatPriceExplanationAmount`, a small local formatter that groups thousands and never parses to
 * a `bigint`; every other amount on this screen (line totals, payments, outstanding) goes through
 * `parseMinorUnits` and `formatMoney`, the same as `OrderList.tsx`.
 *
 * **A negative payment reads as words, not a minus sign.** The task is explicit that a reversal or
 * a refund "must read as one rather than as a confusing negative." Nothing on the wire distinguishes
 * a reversal from a refund, so this screen does not invent that distinction: it says the one thing
 * it actually knows, that the money went back the other way, as "Refund or reversal" next to the
 * amount shown positive. `CL-02`: the word is the requirement, the `--returned` class is only a
 * cue on top of it. Sign is read from the parsed `bigint`, not from `.startsWith('-')`, so a
 * degenerate `"-0"` on the wire cannot read as a reversal of nothing.
 *
 * **A malformed money value throws rather than displaying something wrong.** The same choice
 * `OrderList.tsx` makes, for the same reason: this is server data, not something typed at the
 * counter, and `../money.ts` is "the one place" that parses a wire string, so letting its error
 * propagate is more honest than a local catch that would paper over a real bug in what the server
 * sent.
 *
 * **Money formatting duplicated locally rather than imported from `OrderList.tsx`.** No screen in
 * this app imports from another. `JobDetail.tsx` states the reason directly: duplicating a few
 * lines is cheaper than inventing an inter-screen seam for one caller.
 */

// ---------------------------------------------------------------------------------------------
// Data shapes. Matching what an order-detail endpoint is expected to read, not imported from
// `apps/api`: `apps/*` never import from each other, and there is nothing to import yet regardless.
// ---------------------------------------------------------------------------------------------

export interface OrderDetailLine {
  readonly id: string
  readonly description: string
  readonly quantity: number
  /** `SAL-010`/`SAL-012`: frozen onto the line at order time. */
  readonly saleType: SaleType
  /** `SAL-012`: frozen onto the line at order time, same reasoning as `saleType`. */
  readonly returnsPosition: ReturnsPosition
  /** Minor units, as a string. Never parsed into a `number` in this file (`MR-01`). */
  readonly pricedMinor: string
  /** This line's apportioned share of any order-level discount (`MR-05`), a non-negative reduction. */
  readonly discountMinor: string
  readonly taxMinor: string
  /** The line's settled total. Computed by the server; never derived from the four fields above
   *  here, per `MR-04`: rounding happens at exactly three points and none of them is this screen. */
  readonly totalMinor: string
  /** Appendix V.1's structured breakdown. Present only for a line the pricing engine priced. */
  readonly priceExplanation?: readonly PriceExplanation[]
}

export interface OrderDetailPayment {
  readonly id: string
  readonly method: Tender
  /** Minor units, signed, as a string. Negative is a reversal or a refund. */
  readonly amountMinor: string
  /** ISO 8601 timestamp of when the payment was taken. */
  readonly takenAt: string
  /** `COR-001`'s fourth combination corrects this, so the row has to be able to show it. */
  readonly takenByStaffId: string | null
  readonly takenByName: string | null
}

export interface OrderDetailData {
  readonly id: string
  /**
   * Appendix C.1's state, unnarrowed per `ARC-020`, and the row version Appendix J's lock needs.
   *
   * Carried for what sits beside this screen rather than for what it draws: an offer can only be
   * applied to a `draft`, and the apply takes the version the screen was showing. Before these two
   * the panel had nothing truthful to send.
   */
  readonly status: string
  readonly version: number
  /**
   * The invoice number, or null until `TAX-002` issues one.
   *
   * Null rather than a placeholder: an English string in a data field cannot be translated and
   * leaks into any sentence that interpolates it. The label is this app's, which is what
   * `orders-list.service.ts` asks for in words.
   */
  readonly number: string | null
  /** ISO 4217, shared by every amount on this order. */
  readonly currency: string
  /**
   * Who the sale is for, and which prescription version it was made against.
   *
   * Both were on the wire from the first version of `order-detail.service.ts` and the mapper
   * dropped them, so **no screen holding an order could find its customer**. `SendToLab` is where
   * that stopped being survivable: `JOB-002`'s snapshot is a copy of the Rx at order time, and the
   * only way to the Rx from here is the customer. Null for a walk-in sale, and for a sale raised
   * before a prescription was recorded against it.
   */
  readonly customerId: string | null
  readonly prescriptionId: string | null
  /** The order's sale type, unnarrowed per `ARC-020`. Absent when the server did not send it. */
  readonly saleType?: string
  /**
   * The jobs being made from this sale. `JOB-001`.
   *
   * `jobNumber` and not the id, because `TAX-002a` makes a job number "short, speakable and
   * unambiguous: a customer reads one out over the phone". An empty list means nothing has gone to
   * the lab, which is a different thing from a sale that needs no lab work, and the screen says
   * which.
   */
  readonly jobs: readonly {
    readonly id: string
    readonly jobNumber: string
    readonly status: string
  }[]
  readonly lines: readonly OrderDetailLine[]
  readonly payments: readonly OrderDetailPayment[]
  /** What is still owed, computed by the server and shown as given, never recomputed here (the
   *  same discipline `OrderList.tsx` states for its own `outstandingMinor`). A negative value is a
   *  credit balance, and this screen shows whichever sign arrives rather than assuming it cannot. */
  /**
   * The invoice. Carried because a refunded order has to be able to say what it was for: the wire
   * has always sent it and the mapper used to drop it, so the screen could show what was given back
   * only as a bare number with nothing to read it against.
   */
  readonly totalMinor: string
  readonly outstandingMinor: string
  /**
   * What a refund gave back. Zero on an order that has never been refunded.
   *
   * **Beside the invoice, never netted into it.** `outstandingMinor` above is what the shop can
   * still collect, and on a fully refunded order that is genuinely zero: the goods came back and the
   * money went back, so a balance here would be a debt nobody can pay, and `POS-002` puts that
   * figure on the customer's record and the branch money-owed report. But "Paid in full" is the
   * wrong sentence for a sale that was reversed, which is what this screen used to say. Carrying
   * both figures is what lets it say what actually happened, and `POS-005` deletes no record.
   */
  readonly refundedMinor: string
}

export interface OrderDetailProps {
  readonly order: OrderDetailData
  /**
   * Picks a payment, for the correction panel that sits beside this screen. §6.33 `COR-001`.
   *
   * **This does not make the screen a writer.** It selects, the way the order list selects an
   * order, and the panel that takes the write is `Corrections`, rendered next to this one by
   * `App` exactly as it is for a sale and a job. The header's claim above still holds: nothing
   * here enqueues and nothing here is an input.
   *
   * Absent when the person does not hold `correction_reassign`, which is also when no button is
   * drawn: Appendix B decides whether the door exists, not whether it answers.
   */
  readonly onCorrectPayment?: (paymentId: string) => void
  /**
   * Appendix J: the order's own branch timezone, for the payment times.
   *
   * The screen had none, which is why the one date on it was formatted against the browser.
   */
  readonly branchTimezone: string
}

// ---------------------------------------------------------------------------------------------
// Vocabulary. A shopkeeper's words, not the raw enum strings. The returns position is shared with
// `ReturnLine` through `status-labels.ts`; the two tables below have one caller here.
// ---------------------------------------------------------------------------------------------

/**
 * `orderDetail.saleType.*`, not `newOrder.saleType.*`: the two screens word one sale type
 * differently ("Lenses only, own frame" here, "Lenses only (own frame)" on the order form). The
 * catalogue keeps both so the English each screen has always shown does not change; whether a
 * shopkeeper should meet one name for one sale type is a copy decision for whoever owns the words.
 */
const SALE_TYPE_KEY: Readonly<Record<SaleType, MessageKey>> = {
  complete_spectacles: 'orderDetail.saleType.completeSpectacles',
  lenses_only_own_frame: 'orderDetail.saleType.lensesOnlyOwnFrame',
  frame_only: 'orderDetail.saleType.frameOnly',
  single_lens_replacement: 'orderDetail.saleType.singleLensReplacement',
  plano_sunglasses: 'orderDetail.saleType.planoSunglasses',
  prescription_sunglasses: 'orderDetail.saleType.prescriptionSunglasses',
  ready_readers: 'orderDetail.saleType.readyReaders',
  contact_lenses: 'orderDetail.saleType.contactLenses',
  cl_solutions: 'orderDetail.saleType.clSolutions',
  repair_or_adjustment: 'orderDetail.saleType.repairOrAdjustment',
  spare_parts: 'orderDetail.saleType.spareParts',
  eye_test_only: 'orderDetail.saleType.eyeTestOnly',
}

// ---------------------------------------------------------------------------------------------
// Money. Settled amounts go through `../money.ts`; price-explanation amounts deliberately do not.
// See the header comment for why.
// ---------------------------------------------------------------------------------------------

/**
 * A settled `bigint` amount, thousands-grouped with the currency code in front, e.g.
 * "PKR 1,500.00". The same shape `OrderList.tsx`'s `formatOrderAmount` produces, kept local per
 * this file's header comment.
 */
function formatSettledAmount(minor: bigint, currency: string): string {
  return formatMoneyForDisplay(minor, currency)
}

/**
 * A settled minor-unit wire string to a display string. Delegates to `../money.ts`'s
 * `parseMinorUnits`, so a malformed value fails the same way everywhere in this app (`OrderList.tsx`
 * asserts the same throw). Exported for direct testing.
 */
export function formatOrderDetailAmount(minor: string, currency: string): string {
  return formatSettledAmount(parseMinorUnits(minor), currency)
}

/**
 * Appendix V.1's amounts, and the list that renders them, now live in `price-explanation.tsx`,
 * because `PRC-011` puts the same breakdown on a quote and a second copy of the formatter is a
 * second chance for somebody to reach for `parseMinorUnits`. Re-exported at the name this file
 * has always had it under, so callers and `order-detail.test.tsx` are unaffected.
 */
export { formatPriceExplanationAmount } from './price-explanation.js'

/**
 * **`formatTakenAt` is gone, and `formatDayTime` from `packages/i18n` replaces it.**
 *
 * It read `date.getHours()` off the browser, with a comment saying "this device's local time" as
 * though that settled it: a counter tablet set to the wrong zone, or a branch that is not in the
 * device's zone, reported a payment at the wrong hour and sometimes on the wrong day. It also
 * rendered `2026-10-05 15:05`, which is the wire's format and not one a shop writes (`LOC-004`).
 *
 * Its stated reason for avoiding `toLocaleString`, that a locale-driven format can substitute
 * non-Western digits, was real and is already solved: `formatDisplayDate` asks for the parts and
 * reassembles them, which is why the Urdu interface reads `18 اکتوبر 2026` with Western figures.
 */

// ---------------------------------------------------------------------------------------------
// The screen.
// ---------------------------------------------------------------------------------------------

export function OrderDetail({
  order,
  branchTimezone,
  onCorrectPayment,
}: OrderDetailProps): JSX.Element {
  const { t, locale } = useTranslation()
  const outstanding = parseMinorUnits(order.outstandingMinor)
  const refunded = parseMinorUnits(order.refundedMinor)
  const total = parseMinorUnits(order.totalMinor)
  const refundedInFull = refunded > 0n && refunded >= total
  /**
   * Two sentences, not one sentence with a label dropped into it.
   *
   * `TAX-002` issues the invoice number at a point in the order's life, so a draft has none. The
   * wire used to answer with the English `(not yet invoiced)`, which read as
   * `Order (not yet invoiced)` on a page otherwise in Urdu. Substituting the translated label into
   * the same slot gave `Order Not invoiced yet`, which is worse: the parentheses were what let the
   * old one read as a sentence at all. A sentence with nothing to name is a different sentence.
   */
  const heading =
    order.number === null
      ? t('orderDetail.headingNoNumber')
      : t('orderDetail.heading', { number: order.number })
  const linesCaption =
    order.number === null
      ? t('orderDetail.linesCaptionNoNumber')
      : t('orderDetail.linesCaption', { number: order.number })

  return (
    <section className="lm-screen" aria-labelledby="order-detail-heading">
      <h1 id="order-detail-heading" className="lm-screen__heading">
        {heading}
      </h1>

      <div>
        <h2 className="lm-screen__subheading">{t('common.lines')}</h2>

        {order.lines.length === 0 ? (
          <p className="lm-hint">{t('common.orderNoLines')}</p>
        ) : (
          <table className="lm-line-table">
            <caption className="lm-visually-hidden">
              {linesCaption}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t('orderDetail.col.description')}</th>
                <th scope="col">{t('common.quantity')}</th>
                {/*
                  "Line price", because that is what the figure is: the value beside a quantity of
                  3 is the price of all three. Labelled "Unit price" it read as the price of one
                  frame, and a dispenser quoting from this screen quoted three times the frame's
                  price to a customer.
                  The column was called `unit_price_minor` when that was found, so this header was
                  a correction sitting on top of a name that still said otherwise. Migration 0021
                  renamed it to `priced_minor`, matching `ChainLine.pricedMinor`, which is what
                  `orders.service.ts` has always written into it.
                  Still not divided down to a per-unit figure: `MR-04` fixes rounding at three
                  points and a screen is not one of them.
                */}
                <th scope="col">{t('orderDetail.col.linePrice')}</th>
                <th scope="col">{t('orderDetail.col.discount')}</th>
                <th scope="col">{t('orderDetail.col.tax')}</th>
                <th scope="col">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((line) => {
                const explanation = line.priceExplanation ?? []
                return (
                  <Fragment key={line.id}>
                    <tr>
                      <th scope="row" className="lm-line-table__description">
                        {line.description}
                        <span className="lm-line-table__meta">
                          {t('orderDetail.lineMeta', {
                            saleType: t(SALE_TYPE_KEY[line.saleType]),
                            returnsPosition: returnsPositionLabel(t, line.returnsPosition),
                          })}
                        </span>
                      </th>
                      <td className="lm-line-table__num" dir="ltr">
                        {line.quantity}
                      </td>
                      <td className="lm-line-table__num" dir="ltr">
                        {formatOrderDetailAmount(line.pricedMinor, order.currency)}
                      </td>
                      <td className="lm-line-table__num" dir="ltr">
                        {formatOrderDetailAmount(line.discountMinor, order.currency)}
                      </td>
                      <td className="lm-line-table__num" dir="ltr">
                        {formatOrderDetailAmount(line.taxMinor, order.currency)}
                      </td>
                      <td className="lm-line-table__num" dir="ltr">
                        {formatOrderDetailAmount(line.totalMinor, order.currency)}
                      </td>
                    </tr>
                    {explanation.length > 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <PriceExplanationList
                            t={t}
                            lineId={line.id}
                            description={line.description}
                            entries={explanation}
                            currency={order.currency}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <h2 className="lm-screen__subheading">{t('orderDetail.paymentsHeading')}</h2>

        {order.payments.length === 0 ? (
          <p className="lm-hint">{t('orderDetail.noPayments')}</p>
        ) : (
          <ul className="lm-payment-list">
            {order.payments.map((payment) => {
              // Sign read from the parsed bigint, not the wire string: `"-0"` would pass a
              // `.startsWith('-')` check and read as a reversal of nothing.
              const amount = parseMinorUnits(payment.amountMinor)
              const isReturn = amount < 0n
              const magnitude = isReturn ? -amount : amount
              return (
                <li
                  key={payment.id}
                  className={isReturn ? 'lm-payment lm-payment--returned' : 'lm-payment'}
                >
                  <span className="lm-payment__method">{tenderLabel(t, payment.method)}</span>
                  {isReturn ? (
                    <span className="lm-badge">{t('orderDetail.refundBadge')}</span>
                  ) : null}
                  <span className="lm-payment__amount" dir="ltr">
                    {formatSettledAmount(magnitude, order.currency)}
                  </span>
                  <span className="lm-payment__taken" dir="ltr">
                    {t('orderDetail.takenAt', {
                      takenAt: formatDayTime(payment.takenAt, branchTimezone, locale),
                    })}
                  </span>
                  {/* `BR-08`: attribution is recorded, never inferred, so it is shown. A name the
                      server could not resolve says so rather than being left blank, since blank
                      reads as "nobody has looked". */}
                  <span className="lm-payment__by">
                    {payment.takenByName === null
                      ? t('orderDetail.takenByUnknown')
                      : t('orderDetail.takenBy', { name: payment.takenByName })}
                  </span>
                  {onCorrectPayment === undefined ? null : (
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      // Six payments on one order would otherwise be six buttons with one name.
                      aria-label={t('orderDetail.correctTakerFor', {
                        method: tenderLabel(t, payment.method),
                        amount: formatSettledAmount(magnitude, order.currency),
                      })}
                      onClick={() => {
                        onCorrectPayment(payment.id)
                      }}
                    >
                      {t('orderDetail.correctTaker')}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Each amount goes in as an element, not as text: every figure in these sentences is money,
          and `DESIGN-TOKENS.md` §6 keeps money off the mirroring list, so each one needs its own
          `dir="ltr"` run. `sentence()` is what allows that without splitting the sentence into two
          `t()` calls, which is what Appendix S forbids. */}
      <p className="lm-hint">
        {refundedInFull
          ? sentence(t, 'orderDetail.refundedInFull', {
              total: <Ltr>{formatSettledAmount(total, order.currency)}</Ltr>,
              refunded: <Ltr>{formatSettledAmount(refunded, order.currency)}</Ltr>,
            })
          : refunded > 0n
            ? outstanding > 0n
              ? sentence(t, 'orderDetail.partlyRefundedOutstanding', {
                  refunded: <Ltr>{formatSettledAmount(refunded, order.currency)}</Ltr>,
                  total: <Ltr>{formatSettledAmount(total, order.currency)}</Ltr>,
                  outstanding: <Ltr>{formatSettledAmount(outstanding, order.currency)}</Ltr>,
                })
              : sentence(t, 'orderDetail.partlyRefundedSettled', {
                  refunded: <Ltr>{formatSettledAmount(refunded, order.currency)}</Ltr>,
                  total: <Ltr>{formatSettledAmount(total, order.currency)}</Ltr>,
                })
            : outstanding === 0n
              ? t('orderDetail.paidInFull')
              : outstanding > 0n
                ? sentence(t, 'orderDetail.outstanding', {
                    outstanding: <Ltr>{formatSettledAmount(outstanding, order.currency)}</Ltr>,
                  })
                : sentence(t, 'orderDetail.creditBalance', {
                    credit: <Ltr>{formatSettledAmount(-outstanding, order.currency)}</Ltr>,
                  })}
      </p>
    </section>
  )
}
